import React from 'react';

import {
  ChipField,
  Datagrid,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  TextField,
  useGetOne,
  useRecordContext,
} from 'react-admin';

import { Grid } from '@mui/material';

import ChipStringField from '../../customFields/ChipStringField';
import NoResults from '../layout/NoResults';
import { NationalIdentifierDataGrid } from '../../customFields/NationalIdentifier';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import { IIndividual } from '../../utilities/schemas/individual';
import countries from '../../constants/countries';
import resourceNames from '../../constants/resourceNames';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import entityTypes from '../../constants/entityTypes';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';

const IndividualAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IIndividual>>();
  const { data, isLoading } = useGetOne<AuditTrack<IIndividual>>(`audit-tracks/${record?.id}/previous`, { id: '' });

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IIndividual>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const hasFieldChanges = {
    firstName: auditTracksDiff.resourceData.firstName?.hasChanged,
    lastName: auditTracksDiff.resourceData.lastName?.hasChanged,
    residence: auditTracksDiff.resourceData.residence?.hasChanged,
    email: auditTracksDiff.resourceData.email?.hasChanged,
    phoneNumber: auditTracksDiff.resourceData.phoneNumber?.hasChanged,
    birthDate: auditTracksDiff.resourceData.birthDate?.hasChanged,
    swedishSocialSecurityNumber:
      auditTracksDiff.resourceData.swedishSocialSecurityNumber?.hasChanged,
    riskScore: auditTracksDiff.resourceData.riskScore?.hasChanged,
    scoreLatestCalculationDate:
      auditTracksDiff.resourceData.scoreLatestCalculationDate?.hasChanged,
    isPep: auditTracksDiff.resourceData.isPep?.hasChanged,
    isSanctioned: auditTracksDiff.resourceData.isSanctioned?.hasChanged,
    populationRegisterCollected:
      auditTracksDiff.resourceData.populationRegisterCollected?.hasChanged,
    kycOnboarding: auditTracksDiff.resourceData.kycOnboarding?.hasChanged,
    status: auditTracksDiff.resourceData.status?.hasChanged,
    createdAt: auditTracksDiff.resourceData.createdAt?.hasChanged,
    customIds: auditTracksDiff.resourceData.customIds?.hasChanged,
    relatedCompanies: auditTracksDiff.resourceData.relatedCompanies?.hasChanged,
    currencies: auditTracksDiff.resourceData.currencies?.hasChanged,
    groups: auditTracksDiff.resourceData.groups?.hasChanged,
    nationalIdentifier: auditTracksDiff.resourceData.nationalIdentifier?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={auditTracksDiff}>
          {hasFieldChanges.firstName && (
            <TextDiffField source="resourceData.firstName" label="First name" />
          )}
          {hasFieldChanges.lastName && (
            <TextDiffField source="resourceData.lastName" label="Last name" />
          )}
          {hasFieldChanges.residence && (
            <SelectDiffField source="resourceData.residence" label="Residence" choices={countries} />
          )}
          {hasFieldChanges.email && (
            <TextDiffField source="resourceData.email" label="Email" />
          )}
          {hasFieldChanges.phoneNumber && (
            <TextDiffField source="resourceData.phoneNumber" label="Phone number" />
          )}
          {hasFieldChanges.birthDate && (
            <DateDiffField source="resourceData.birthDate" label="Date of birth" options={{ timeZone: 'UTC' }} />
          )}
          {hasFieldChanges.swedishSocialSecurityNumber && (
            <TextDiffField source="resourceData.swedishSocialSecurityNumber" label="Social security number" />
          )}
          {hasFieldChanges.riskScore && (
            <TextDiffField source="resourceData.riskScore" label="Risk score" />
          )}
          {hasFieldChanges.scoreLatestCalculationDate && (
            <DateDiffField source="resourceData.scoreLatestCalculationDate" label="Score latest calculation date" />
          )}
          {hasFieldChanges.isPep && (
            <TextDiffField source="resourceData.isPep" label="is Pep" />
          )}
          {hasFieldChanges.isSanctioned && (
            <TextDiffField source="resourceData.isSanctioned" label="is Sanctioned" />
          )}
          {hasFieldChanges.populationRegisterCollected && (
            <TextDiffField source="resourceData.populationRegisterCollected" label="populationRegisterCollected" />
          )}
          {hasFieldChanges.kycOnboarding && (
            <DateDiffField source="resourceData.kycOnboarding" label="KYC onboarding" />
          )}
          {hasFieldChanges.status && (
            <StatusDiffField source="resourceData.status" label="status" />
          )}
          {hasFieldChanges.createdAt && (
            <DateDiffField source="resourceData.createdAt" label="createdAt" />
          )}
          {hasFieldChanges.customIds && (
            <ArrayDiffField source="resourceData.customIds" label="Custom IDs">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}
          {hasFieldChanges.relatedCompanies && (
            <ArrayDiffField source="resourceData.relatedCompanies" label="Related companies">
              <Datagrid
                sx={boldDataGridStyle}
                empty={<NoResults />}
              >
                <ReferenceEntityField
                  source=""
                  label="Business"
                  idKey="companyId"
                  entityType={entityTypes.BUSINESS}
                >
                  <EntityDisplayNameField />
                </ReferenceEntityField>
                <TextField source="roles" sortable={false} />
              </Datagrid>
            </ArrayDiffField>
          )}
          {hasFieldChanges.currencies && (
            <ArrayDiffField source="currencies" label="Currencies">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}
          {hasFieldChanges.groups && (
            <ReferenceArrayDiffField
              source="resourceData.groups"
              label="Groups"
              reference={resourceNames.GROUPS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}
          {hasFieldChanges.nationalIdentifier && (
            <ArrayDiffField source="resourceData.nationalIdentifier" label="National identifier">
              <NationalIdentifierDataGrid />
            </ArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default IndividualAuditDetails;
